import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  addEventToCalendar,
  type CreateEventInput,
  type AddEventOptions,
  type AddEventResult,
} from '../lib/addEventToCalendar';
import type { Event } from 'commoncal-shared';

export function useEvents(
  calendarId: string | undefined,
  from: string,
  to: string
) {
  return useQuery({
    queryKey: ['events', calendarId, from, to],
    queryFn: () =>
      calendarId
        ? api.events.list(calendarId, { from, to })
        : Promise.resolve(null),
    enabled: Boolean(calendarId),
    select: data => data?.events ?? [],
  });
}

export function useAddEvent(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation<
    AddEventResult,
    Error,
    { input: CreateEventInput; options?: AddEventOptions }
  >({
    mutationFn: ({ input, options }) =>
      addEventToCalendar(input, options),
    onSuccess: ({ event }) => {
      qc.setQueryData<Event[]>(
        ['events', calendarId],
        old => (old ? [...old, event] : [event])
      );
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({
        queryKey: ['events', vars.input.calendar_id],
      });
    },
  });
}

export function useUpdateEvent(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: Partial<{
      title: string;
      description: string | null;
      address: string | null;
      phone: string | null;
      start_at: string;
      end_at: string;
      start_date: string;
      end_date: string;
      all_day: boolean;
      rrule: string | null;
      exdate: string[];
      tags: string[];
    }> & { id: string }) => {
      console.log(
        '[useUpdateEvent] sending PATCH /events/' + id,
        JSON.stringify(body)
      );
      return api.events.update(id, body);
    },
    onMutate: async ({ id, ...patch }) => {
      const key = ['events', calendarId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Event[]>(key);
      qc.setQueryData<Event[]>(key, old =>
        old
          ? old.map(e =>
              e.id === id ? { ...e, ...patch } : e
            )
          : old
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          ['events', calendarId],
          ctx.previous
        );
      }
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({
        queryKey: ['events', calendarId],
      });
      // Invalidate the individual event query so EventPanel gets fresh data
      qc.invalidateQueries({
        queryKey: ['event', vars.id],
      });
    },
  });
}

export type DeleteScope = 'all' | 'future' | 'this';

export function useDeleteEvent(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      scope,
      occurrenceDate,
    }: {
      id: string;
      scope: DeleteScope;
      occurrenceDate?: string;
    }) => api.events.delete(id, scope, occurrenceDate),
    onMutate: async ({ id }) => {
      const key = ['events', calendarId];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Event[]>(key);
      qc.setQueryData<Event[]>(key, old =>
        old ? old.filter(e => e.id !== id) : old
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          ['events', calendarId],
          ctx.previous
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({
        queryKey: ['events', calendarId],
      });
    },
  });
}

interface CreateExceptionInput {
  calendar_id: string;
  title: string;
  description?: string;
  start_at: string;
  end_at: string;
  start_date?: string;
  end_date?: string;
  all_day: boolean;
  recurrence_id: string;
  original_occurrence_date: string;
}

export function useCreateExceptionEvent(
  templateCalendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateExceptionInput) =>
      api.events.create(input),
    onSuccess: () => {
      // Invalidate both the template calendar and the exception's calendar
      qc.invalidateQueries({
        queryKey: ['events', templateCalendarId],
      });
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({
        queryKey: ['events', vars.calendar_id],
      });
    },
  });
}

export function useExcludeException(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (exceptionId: string) =>
      api.events.exceptionExclude(exceptionId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['events', calendarId],
      });
    },
  });
}

export function useRestoreException(
  calendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (exceptionId: string) =>
      api.events.exceptionRestore(exceptionId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['events', calendarId],
      });
    },
  });
}

export function useMirrorEvent(
  _sourceCalendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      targetCalendarId,
    }: {
      eventId: string;
      targetCalendarId: string;
    }) => api.events.mirror(eventId, targetCalendarId),
    onSuccess: (_data, vars) => {
      // Invalidate target calendar's events to show the new mirror
      qc.invalidateQueries({
        queryKey: ['events', vars.targetCalendarId],
      });
      // Invalidate the event's mirror list
      qc.invalidateQueries({
        queryKey: ['event-mirrors', vars.eventId],
      });
    },
  });
}

export function useUnmirrorEvent(
  _sourceCalendarId: string | undefined
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      targetCalendarId,
    }: {
      eventId: string;
      targetCalendarId: string;
    }) => api.events.unmirror(eventId, targetCalendarId),
    onSuccess: (_data, vars) => {
      // Invalidate target calendar's events to remove the mirror
      qc.invalidateQueries({
        queryKey: ['events', vars.targetCalendarId],
      });
      // Invalidate the event's mirror list
      qc.invalidateQueries({
        queryKey: ['event-mirrors', vars.eventId],
      });
    },
  });
}
